import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatINR, formatDate, API, istDate } from "@/lib/api";
import { Pencil, Trash2, Save, X, Download, Search, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import MonthSelect from "@/components/MonthSelect";
import ConfirmDialog from "@/components/ConfirmDialog";
import { FilterSummary, PageHeader, StatusBadge } from "@/components/ui/AppPrimitives";

export default function DonationsTab() {
  const initialView = readDonationView();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialView.search || "");
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empFilter, setEmpFilter] = useState(initialView.empFilter || "");
  const [statusFilter, setStatusFilter] = useState(initialView.statusFilter || "");
  const [sourceFilter, setSourceFilter] = useState(initialView.sourceFilter || "");
  const [orgFilter, setOrgFilter] = useState(initialView.orgFilter || "");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [constants, setConstants] = useState({ sources: [], organizations: [] });
  const [dateFilter, setDateFilter] = useState(initialView.dateFilter || "");
  const [monthFilter, setMonthFilter] = useState(initialView.monthFilter || "");
  const [sort, setSort] = useState(initialView.sort || "newest");
  const [confirmAction, setConfirmAction] = useState(null);
  const [matrixStart, setMatrixStart] = useState(istDate());
  const [matrixEnd, setMatrixEnd] = useState(istDate());
  const [matrixMode, setMatrixMode] = useState("single");
  const [matrix, setMatrix] = useState({ organizations: [], rows: [], totals: {}, grand_total: 0 });
  const [matrixUpdatedAt, setMatrixUpdatedAt] = useState(null);
  const [page, setPage] = useState(Number(initialView.page) || 1);
  const [pageSize, setPageSize] = useState(Number(initialView.pageSize) || 50);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, total_amount: 0 });

  useEffect(() => {
    sessionStorage.setItem("kaya:admin-donations-view", JSON.stringify({ search, empFilter, statusFilter, sourceFilter, orgFilter, dateFilter, monthFilter, sort, page, pageSize }));
  }, [search, empFilter, statusFilter, sourceFilter, orgFilter, dateFilter, monthFilter, sort, page, pageSize]);

  const loadMatrix = ({ silent = false } = {}) => api.get(`/admin/donations/collection-matrix?start=${matrixStart}&end=${matrixMode === "single" ? matrixStart : matrixEnd}`)
    .then((r) => { setMatrix(r.data); setMatrixUpdatedAt(new Date()); })
    .catch((er) => { if (!silent) toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message); });

  const load = async ({ background = false, silent = false } = {}) => {
    if (minAmount !== "" && maxAmount !== "" && Number(minAmount) > Number(maxAmount)) {
      toast.error("Minimum amount cannot exceed maximum amount");
      setLoading(false);
      return;
    }
    if (!background) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (empFilter) params.set("employee_id", empFilter);
      if (statusFilter === "unverified") params.set("approved", "false");
      if (statusFilter === "verified") params.set("approved", "true");
      if (statusFilter === "clash") params.set("clash", "true");
      if (statusFilter === "repeated") params.set("repeated", "true");
      if (statusFilter === "rejected") params.set("rejected", "true");
      if (statusFilter === "shared") params.set("shared", "true");
      if (sourceFilter) params.set("source", sourceFilter);
      if (orgFilter) params.set("organization", orgFilter);
      if (minAmount !== "") params.set("min_amount", minAmount);
      if (maxAmount !== "") params.set("max_amount", maxAmount);
      if (dateFilter) params.set("date", dateFilter);
      if (!dateFilter && monthFilter) {
        if (/^\d{4}$/.test(monthFilter)) { params.set("start", `${monthFilter}-01-01`); params.set("end", `${monthFilter}-12-31`); }
        else params.set("month", monthFilter);
      }
      if (sort) params.set("sort", sort);
      params.set("paginated", "true");
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const [d, e, c] = await Promise.all([
        api.get(`/donations?${params}`),
        employees.length ? Promise.resolve({ data: employees }) : api.get("/admin/employees"),
        constants.sources.length || constants.organizations.length ? Promise.resolve({ data: constants }) : api.get("/admin/constants"),
      ]);
      setItems((current) => reconcileDonations(current, d.data.items || []));
      setPagination({ total: d.data.total || 0, pages: d.data.pages || 1, total_amount: d.data.total_amount || 0 });
      if (!employees.length) setEmployees(e.data);
      if (!constants.sources.length && !constants.organizations.length) {
        setConstants({ sources: c.data.sources || [], organizations: c.data.organizations || [] });
      }
    } catch (er) {
      if (!silent) toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally { if (!background) setLoading(false); }
  };
  // Initial fetch only; subsequent refreshes are handled by useAutoRefresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadMatrix(); }, []);
  useEffect(() => {
    window.addEventListener("kaya:clashes-updated", load);
    return () => window.removeEventListener("kaya:clashes-updated", load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, empFilter, statusFilter, sourceFilter, orgFilter, minAmount, maxAmount, dateFilter, monthFilter, sort, page, pageSize]);
  useEffect(() => { load(); }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => load({ background: true, silent: true }), [search, empFilter, statusFilter, sourceFilter, orgFilter, minAmount, maxAmount, dateFilter, monthFilter, sort, page, pageSize], {
    enabled: !editing,
    refreshOnFocus: false,
    intervalMs: 10000,
  });
  useAutoRefresh(() => loadMatrix({ silent: true }), [matrixStart, matrixEnd, matrixMode], {
    intervalMs: 10000,
    refreshOnFocus: true,
  });

  const save = async () => {
    try {
      await api.patch(`/donations/${editing.id}`, {
        donor_name: editing.donor_name,
        donor_phone: editing.donor_phone,
        amount: parseFloat(editing.amount),
        donor_email: editing.donor_email,
        notes: editing.notes,
      });
      toast.success("Donation updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/donations/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const approve = async (id) => {
    try {
      await api.patch(`/donations/${id}`, { approved: true });
      toast.success("Verified");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const clearClash = async (id) => {
    try {
      await api.post(`/admin/donations/${id}/resolve-clash`, { action: "clear" });
      toast.success("Clash removed");
      window.dispatchEvent(new Event("kaya:clashes-updated"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const activeFilterCount = [search, empFilter, statusFilter, sourceFilter, orgFilter, minAmount, maxAmount, dateFilter, monthFilter].filter((value) => value !== "").length;
  const clearFilters = () => {
    setSearch(""); setEmpFilter(""); setStatusFilter(""); setSourceFilter("");
    setOrgFilter(""); setMinAmount(""); setMaxAmount(""); setDateFilter(""); setMonthFilter(""); setSort("newest");
    setPage(1);
  };

  const runConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action?.type === "delete") await remove(action.id);
    if (action?.type === "clearClash") await clearClash(action.id);
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ _t: String(Date.now()) });
    if (search) params.set("search", search);
    if (empFilter) params.set("employee_id", empFilter);
    if (sourceFilter) params.set("source", sourceFilter);
    if (orgFilter) params.set("organization", orgFilter);
    if (dateFilter) { params.set("start", dateFilter); params.set("end", dateFilter); }
    if (!dateFilter && monthFilter) {
      if (/^\d{4}$/.test(monthFilter)) { params.set("start", `${monthFilter}-01-01`); params.set("end", `${monthFilter}-12-31`); }
      else { params.set("start", `${monthFilter}-01`); params.set("end", `${monthFilter}-31`); }
    }
    if (minAmount !== "") params.set("min_amount", minAmount);
    if (maxAmount !== "") params.set("max_amount", maxAmount);
    if (statusFilter === "verified") params.set("approved", "true");
    if (statusFilter === "unverified") params.set("approved", "false");
    if (statusFilter === "clash") params.set("clash", "true");
    if (statusFilter === "repeated") params.set("repeated", "true");
    if (statusFilter === "rejected") params.set("rejected", "true");
    if (statusFilter === "shared") params.set("shared", "true");
    params.set("sort", sort);
    const url = `${API}/admin/export/donations?${params}`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "donations.csv";
        a.click();
      });
  };

  return (
    <div className="space-y-4" data-testid="admin-donations">
      <PageHeader eyebrow="Donations" title="Donation management" description="Review collections, verify records, and export donation data." />
      <div className="card-warm overflow-hidden rounded-b-none" data-testid="employee-collection-matrix">
        <div className="p-5 border-b border-[#EAE6DF] flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-overline">Collection summary</div>
            <h2 className="text-xl font-medium text-[#2D312E]">Employee collection by organization</h2>
          </div>
          <div className="rounded-2xl bg-[#4A5D4E] text-white px-6 py-4 min-w-[210px] shadow-sm" data-testid="admin-total-collection">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/70">Total collection</div>
            <div className="text-3xl font-semibold mt-1" style={{ fontFamily: "Manrope" }}>{formatINR(matrix.grand_total)}</div>
            <div className="text-xs text-white/70 mt-1">{formatDate(matrix.start)}{matrix.start !== matrix.end ? ` – ${formatDate(matrix.end)}` : ""}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border border-[#EAE6DF] overflow-hidden">
              <button type="button" onClick={() => setMatrixMode("single")} className={`px-3 py-2 text-xs ${matrixMode === "single" ? "bg-[#4A5D4E] text-white" : "bg-[#FDFBF7] text-[#5C635E]"}`}>Single date</button>
              <button type="button" onClick={() => setMatrixMode("range")} className={`px-3 py-2 text-xs ${matrixMode === "range" ? "bg-[#4A5D4E] text-white" : "bg-[#FDFBF7] text-[#5C635E]"}`}>Date range</button>
            </div>
            <label className="text-xs text-[#5C635E]">{matrixMode === "single" ? "Date" : "From"} <input type="date" value={matrixStart} onChange={(e) => { setMatrixStart(e.target.value); if (matrixMode === "single") setMatrixEnd(e.target.value); }} className="ml-1 border border-[#EAE6DF] rounded-lg px-3 py-2 bg-[#FDFBF7]" /></label>
            {matrixMode === "range" && <label className="text-xs text-[#5C635E]">To <input type="date" value={matrixEnd} min={matrixStart} onChange={(e) => setMatrixEnd(e.target.value)} className="ml-1 border border-[#EAE6DF] rounded-lg px-3 py-2 bg-[#FDFBF7]" /></label>}
            <button onClick={() => loadMatrix()} className="bg-[#4A5D4E] text-white rounded-lg px-4 py-2 text-sm">Apply</button>
            <span className="text-[11px] text-[#8A938D]" role="status">Auto-updates every 10s{matrixUpdatedAt ? ` · Updated ${matrixUpdatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" aria-label="Employee collections by organization">
            <caption className="sr-only">Employee collections by organization for the selected period</caption>
            <thead><tr className="bg-[#F4F1EA] text-xs uppercase tracking-wide text-[#5C635E]">
              <th className="sticky left-0 z-30 w-[108px] min-w-[108px] sm:w-auto sm:min-w-[210px] bg-[#F4F1EA] p-2 sm:p-3 text-left text-[10px] sm:text-xs shadow-[2px_0_0_#EAE6DF]">Employee</th>
              {matrix.organizations.map((org) => <th key={org} className="min-w-[130px] p-3 text-right">{org}</th>)}
              <th className="sticky right-0 z-30 w-[92px] min-w-[92px] sm:w-auto sm:min-w-[145px] bg-[#E9EEE9] p-2 sm:p-3 text-right text-[10px] sm:text-xs shadow-[-2px_0_0_#D8DED9]"><span className="sm:hidden">Total</span><span className="hidden sm:inline">Employee total</span></th>
            </tr></thead>
            <tbody>
              {matrix.rows.length === 0 ? <tr><td colSpan={matrix.organizations.length + 2} className="p-6 text-center text-[#8A938D]">No employees found.</td></tr> : matrix.rows.map((row, rowIndex) => {
                const rowBg = row.status === "absent" ? "bg-red-50" : row.status === "on_leave" ? "bg-violet-50" : rowIndex % 2 ? "bg-[#FDFBF7]" : "bg-white";
                return <tr key={row.employee_id} className={`group border-t border-[#EAE6DF] ${rowBg} hover:bg-[#F4F1EA]`}>
                  <td className={`sticky left-0 z-10 w-[108px] min-w-[108px] sm:w-auto sm:min-w-[210px] p-2 sm:p-3 shadow-[2px_0_0_#EAE6DF] group-hover:bg-[#F4F1EA] ${rowBg}`}>
                    <div className="font-semibold text-xs sm:text-sm leading-tight text-[#2D312E] break-words">{row.employee_name}</div>
                    {row.status && row.status !== "present" && <div className="mt-1"><StatusBadge status={row.status} /></div>}
                  </td>
                  {matrix.organizations.map((org) => {
                    const amount = Number(row.organizations[org] || 0);
                    return <td key={org} className={`p-3 text-right tabular-nums ${amount ? "font-medium text-[#2D312E]" : "text-[#A5ABA7]"}`}>
                      {amount ? formatINR(amount) : "—"}
                    </td>;
                  })}
                  <td className={`sticky right-0 z-10 w-[92px] min-w-[92px] sm:w-auto sm:min-w-[145px] p-2 sm:p-3 text-right shadow-[-2px_0_0_#D8DED9] group-hover:bg-[#E9EEE9] ${row.total ? "bg-[#F0F5F0]" : rowBg}`}>
                    {row.total ? <span className="text-xs sm:text-base font-bold tabular-nums text-[#3F5544]">{formatINR(row.total)}</span> : row.status === "on_leave" ? <StatusBadge status="on_leave" /> : row.status === "absent" ? <StatusBadge status="absent" /> : <span className="font-semibold text-[#8A938D]">₹0</span>}
                  </td>
                </tr>;
              })}
            </tbody>
            <tfoot><tr className="border-t-2 border-[#4A5D4E] bg-[#E9EEE9] font-semibold">
              <td className="sticky bottom-0 left-0 z-40 w-[108px] min-w-[108px] sm:w-auto sm:min-w-[210px] bg-[#E9EEE9] p-2 sm:p-3 text-xs sm:text-sm shadow-[2px_0_0_#D8DED9]">All employees</td>
              {matrix.organizations.map((org) => <td key={org} className="sticky bottom-0 z-20 bg-[#E9EEE9] p-3 text-right tabular-nums">{formatINR(matrix.totals[org] || 0)}</td>)}
              <td className="sticky bottom-0 right-0 z-40 w-[92px] min-w-[92px] sm:w-auto sm:min-w-[145px] bg-[#DCE7DD] p-2 sm:p-3 text-right text-xs sm:text-base font-bold tabular-nums text-[#3F5544] shadow-[-2px_0_0_#CBD7CC]">{formatINR(matrix.grand_total)}</td>
            </tr></tfoot>
          </table>
        </div>
      </div>
      <div className="card-warm overflow-hidden" data-testid="donations-list-section">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur p-4 flex flex-wrap items-center gap-3 border-b border-[#EAE6DF]">
        <div className="basis-full flex items-center justify-between gap-3"><div><div className="text-overline">Records</div><h3 className="text-lg font-medium text-[#2D312E]">Search and filter donations</h3></div><div className="text-xs text-[#8A938D]">Filters combine together</div></div>
        <div className="flex items-center gap-2 flex-1 min-w-[260px] border border-[#EAE6DF] rounded-lg px-3 py-2 bg-[#FDFBF7]">
          <Search className="h-4 w-4 text-[#8A938D]" />
          <input
            data-testid="donations-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by donor name, phone, receipt…"
            className="flex-1 bg-transparent focus:outline-none text-sm text-[#2D312E] placeholder-[#8A938D]"
          />
        </div>
        <select
          data-testid="donations-employee-filter"
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E]"
        >
          <option value="">All employees</option>
          {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
        </select>
        <select
          data-testid="donations-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E]"
        >
          <option value="">All statuses</option>
          <option value="verified">Verified</option>
          <option value="unverified">Unverified</option>
          <option value="clash">Clash</option>
          <option value="repeated">Repeated</option>
          <option value="shared">Shared</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          data-testid="donations-source-filter"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E] uppercase"
        >
          <option value="">All sources</option>
          {(constants.sources || []).map((src) => <option key={src} value={src}>{String(src).toUpperCase()}</option>)}
        </select>
        <select
          data-testid="donations-org-filter"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E] uppercase"
        >
          <option value="">All orgs</option>
          {(constants.organizations || []).map((org) => <option key={org} value={org}>{String(org).toUpperCase()}</option>)}
        </select>
        <input
          data-testid="donations-date-filter"
          type="date"
          value={dateFilter}
          onChange={(e) => { setDateFilter(e.target.value); if (e.target.value) setMonthFilter(""); }}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E]"
        />
        <MonthSelect testId="donations-month-filter" value={monthFilter} allowClear allowYearOnly onChange={(value) => { setMonthFilter(value); if (value) setDateFilter(""); }} />
        <input data-testid="donations-min-amount" type="number" min="0" inputMode="decimal" value={minAmount}
          onWheel={(e) => e.currentTarget.blur()} onChange={(e) => setMinAmount(e.target.value)} placeholder="Min amount"
          className="w-32 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E]" />
        <input data-testid="donations-max-amount" type="number" min="0" inputMode="decimal" value={maxAmount}
          onWheel={(e) => e.currentTarget.blur()} onChange={(e) => setMaxAmount(e.target.value)} placeholder="Max amount"
          className="w-32 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E]" />
        <select
          data-testid="donations-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm text-[#2D312E]"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="amount_desc">Amount: high to low</option>
          <option value="amount_asc">Amount: low to high</option>
          <option value="name_asc">Donor name A–Z</option>
          <option value="status">Needs attention first</option>
        </select>
        <button data-testid="donations-apply-filter" onClick={() => { if (page === 1) load(); else setPage(1); }}
          className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-lg px-4 py-2 text-sm">Apply</button>
        {activeFilterCount > 0 && <button type="button" data-testid="donations-clear-filters" onClick={clearFilters} className="border border-[#EAE6DF] hover:border-[#C05746] hover:text-[#C05746] rounded-lg px-4 py-2 text-sm text-[#5C635E]">Clear filters ({activeFilterCount})</button>}
        <button data-testid="donations-export-csv" onClick={exportCsv}
          className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E] flex items-center gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </button>
        <div className="basis-full">
          <FilterSummary filters={[
            { key: "search", label: "Search", value: search, onRemove: () => setSearch("") },
            { key: "employee", label: "Employee", value: empFilter, display: employees.find((e) => e.id === empFilter)?.name, onRemove: () => setEmpFilter("") },
            { key: "status", label: "Status", value: statusFilter, onRemove: () => setStatusFilter("") },
            { key: "source", label: "Source", value: sourceFilter, onRemove: () => setSourceFilter("") },
            { key: "org", label: "Organization", value: orgFilter, onRemove: () => setOrgFilter("") },
            { key: "date", label: "Date", value: dateFilter, display: dateFilter && formatDate(dateFilter), onRemove: () => setDateFilter("") },
            { key: "period", label: "Period", value: monthFilter, onRemove: () => setMonthFilter("") },
          ]} onClearAll={clearFilters} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between p-4 border-b border-[#EAE6DF] gap-3 flex-wrap">
          <div className="text-sm text-[#5C635E]">Showing {items.length} of {pagination.total} donations{activeFilterCount > 0 ? ` · ${activeFilterCount} active filter${activeFilterCount === 1 ? "" : "s"}` : ""}</div>
          <div className="rounded-xl bg-[#4A5D4E] text-white px-5 py-3" data-testid="filtered-donation-total"><div className="text-[10px] uppercase tracking-wider text-white/70">Filtered total</div><div className="text-xl font-semibold">{formatINR(pagination.total_amount)}</div></div>
        </div>
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[980px] text-sm" aria-label="Donation records">
            <caption className="sr-only">Donation records matching the active filters</caption>
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="text-left p-3 font-semibold">Receipt / Date</th>
                <th className="text-left p-3 font-semibold">Donor</th>
                <th className="text-left p-3 font-semibold">Employee</th>
                <th className="text-left p-3 font-semibold">Org</th>
                <th className="text-left p-3 font-semibold">Source</th>
                <th className="text-right p-3 font-semibold">Amount</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-[#8A938D]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-[#8A938D]">No donations yet.</td></tr>
              ) : items.map((d) => (
                <tr key={d.id} data-testid={`donation-row-${d.id}`} className="border-t border-[#EAE6DF] hover:bg-[#FDFBF7]">
                  {editing?.id === d.id ? (
                    <>
                      <td className="p-3 text-xs font-mono text-[#8A938D]">{d.receipt_number}<div>{formatDate(d.created_at)}</div></td>
                      <td className="p-3">
                        <input className="w-full border border-[#EAE6DF] rounded px-2 py-1 mb-1" value={editing.donor_name} onChange={(e) => setEditing({ ...editing, donor_name: e.target.value })} />
                        <input className="w-full border border-[#EAE6DF] rounded px-2 py-1" value={editing.donor_phone} onChange={(e) => setEditing({ ...editing, donor_phone: e.target.value })} />
                      </td>
                      <td className="p-3">{d.employee_name}</td>
                      <td className="p-3 uppercase text-[#5C635E]">{d.organization || "—"}</td>
                      <td className="p-3 uppercase text-[#5C635E]">{d.source || "—"}</td>
                      <td className="p-3">
                        <input type="number" className="w-28 border border-[#EAE6DF] rounded px-2 py-1 text-right" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} />
                      </td>
                      <td className="p-3 text-right">
                        <button data-testid={`donation-save-${d.id}`} onClick={save} className="text-emerald-700 mr-2"><Save className="h-4 w-4 inline" /></button>
                        <button onClick={() => setEditing(null)} className="text-[#8A938D]"><X className="h-4 w-4 inline" /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3">
                        <div className="text-xs font-mono text-[#8A938D]">{d.receipt_number}</div>
                        <div className="text-xs text-[#2D312E] font-medium" title="Donation date (for)">
                          For: {formatDate(d.donation_date || d.created_at)}
                        </div>
                        <div className="text-[10px] text-[#8A938D]">Added: {formatDate(d.created_at)}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-[#2D312E]">{d.donor_name}</div>
                        <div className="text-xs text-[#8A938D]">{d.donor_phone}</div>
                        {d.shared_group_id && (
                          <div className="text-[10px] text-[#4A5D4E] mt-1">Shared with {d.shared_with_employee_name} from {formatINR(d.shared_total_amount)}</div>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {d.approved && d.verified_by === "sheet" && (
                            <span data-testid={`verified-sheet-${d.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title={d.verified_source ? `From ${d.verified_source}` : "Verified via Google Sheet"}>
                              ✓ Sheet {d.verified_source ? `· ${d.verified_source}` : ""}
                            </span>
                          )}
                          {d.approved && d.verified_by !== "sheet" && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Verified</span>
                          )}
                          {!d.approved && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Unverified</span>
                          )}
                          {d.repeated_donation && (
                            <span data-testid={`repeated-donation-${d.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              Repeated
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-[#5C635E]">{d.employee_name}</td>
                      <td className="p-3 uppercase text-[#5C635E]">{d.organization || "—"}</td>
                      <td className="p-3 uppercase text-[#5C635E]">{d.source || "—"}</td>
                      <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(d.amount)}</td>
                      <td className="p-3 text-right">
                        {!d.approved && (
                          <button data-testid={`donation-approve-${d.id}`} onClick={() => approve(d.id)} className="text-emerald-700 hover:text-emerald-800 mr-3" title="Verify"><CheckCircle2 className="h-4 w-4 inline" /></button>
                        )}
                        {d.clash && (
                          <>
                            <span data-testid="clash-indicator" className="mr-2 text-red-700 text-xs font-semibold">⚠ Clash</span>
                            <button data-testid={`donation-clear-clash-${d.id}`} onClick={() => setConfirmAction({ type: "clearClash", id: d.id })} className="text-red-700 hover:text-red-800 text-xs underline mr-3">
                              Clear clash
                            </button>
                          </>
                        )}
                        <button data-testid={`donation-edit-${d.id}`} onClick={() => setEditing({ ...d })} className="text-[#5C635E] hover:text-[#4A5D4E] mr-3"><Pencil className="h-4 w-4 inline" /></button>
                        <button data-testid={`donation-delete-${d.id}`} onClick={() => setConfirmAction({ type: "delete", id: d.id })} className="text-[#C05746] hover:text-[#A34A3C]"><Trash2 className="h-4 w-4 inline" /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap border-t border-[#EAE6DF] p-4">
          <div className="text-sm text-[#5C635E]">{pagination.total ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, pagination.total)} of ${pagination.total}` : "No results"} · Page {page} of {pagination.pages}</div>
          <div className="flex items-center gap-2">
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm bg-white" aria-label="Donations per page">
              {[25, 50, 100, 200].map(size => <option key={size} value={size}>{size} per page</option>)}
            </select>
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border border-[#EAE6DF] rounded-lg px-4 py-2 text-sm disabled:opacity-40">Previous</button>
            <button type="button" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} className="border border-[#EAE6DF] rounded-lg px-4 py-2 text-sm disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
      </div>
      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === "clearClash" ? "Remove clash status?" : "Delete donation?"}
        message={confirmAction?.type === "clearClash" ? "Remove clash status for this donation?" : "Delete this donation?"}
        confirmLabel={confirmAction?.type === "clearClash" ? "Remove clash" : "Delete"}
        onConfirm={runConfirmAction}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

function readDonationView() {
  try { return JSON.parse(sessionStorage.getItem("kaya:admin-donations-view") || "{}"); }
  catch { return {}; }
}

// Preserve unchanged row objects during background refreshes. React can then keep
// every existing keyed table row in place and only insert/update/remove rows whose
// server data actually changed (for example, one newly submitted donation).
function reconcileDonations(current, incoming) {
  const currentById = new Map(current.map((item) => [item.id, item]));
  return incoming.map((item) => {
    const existing = currentById.get(item.id);
    return existing && shallowEqual(existing, item) ? existing : item;
  });
}

function shallowEqual(left, right) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key) => left[key] === right[key]);
}
